import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  CalendarDays,
  ChevronLeft,
  Globe2,
  Layers3,
  Phone,
  RefreshCw,
  Search,
  Sparkles,
  UserCircle2,
} from "lucide-react";
import {
  getMatchPublishHistory,
  getMatchPublishSummary,
  getMatchmakingResultsFeed,
} from "./apis/get";
import { buildQueryString } from "../utilities/queryParams";
import MatchesCharts from "./matches/MatchesCharts";
import MatchesFeedTable from "./matches/MatchesFeedTable";
import MatchesHistoryTable from "./matches/MatchesHistoryTable";

const DEFAULT_LIMIT = "20";
const DEFAULT_FILTERS = {
  uid: "",
  user_id: "",
  candidate_id: "",
  search: "",
  compatibility_level: "",
  source_service: "",
  decision: "all",
  is_disliked: "all",
  published: "true",
  today: "false",
  new: "all",
  distance: "",
  match_score_min: "",
  match_score_max: "",
  published_from: "",
  published_to: "",
  created_from: "",
  created_to: "",
  group_by: "day",
  sort_by: "published_at",
  sort_order: "desc",
  limit: DEFAULT_LIMIT,
  offset: "0",
};

const COMPATIBILITY_OPTIONS = [
  { value: "", label: "All levels" },
  { value: "EXCELLENT", label: "Excellent" },
  { value: "VERY_GOOD", label: "Very Good" },
  { value: "GOOD", label: "Good" },
  { value: "FAIR", label: "Fair" },
  { value: "POOR", label: "Poor" },
];

const GROUP_BY_OPTIONS = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];

const SORT_BY_OPTIONS = [
  { value: "published_at", label: "Published At" },
  { value: "created_at", label: "Created At" },
  { value: "updated_at", label: "Updated At" },
  { value: "match_score", label: "Match Score" },
  { value: "candidate_name", label: "Candidate Name" },
];

const SORT_ORDER_OPTIONS = [
  { value: "desc", label: "Newest first" },
  { value: "asc", label: "Oldest first" },
];

const BOOLEAN_TOGGLE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "true", label: "True" },
  { value: "false", label: "False" },
];

const getString = (value) => (value === undefined || value === null ? "" : String(value));
const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};
const formatLabel = (value) =>
  getString(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const extractPayload = (response) => response?.data || response || {};

const normalizeBreakdown = (input) => {
  if (Array.isArray(input)) {
    return input
      .map((item, index) => ({
        name: item?.name || item?.label || item?.key || item?.service || `Item ${index + 1}`,
        value: toNumber(item?.value ?? item?.count ?? item?.total ?? item),
      }))
      .filter((item) => item.name);
  }

  if (input && typeof input === "object") {
    return Object.entries(input).map(([key, value]) => ({
      name: formatLabel(key),
      value: toNumber(value?.value ?? value?.count ?? value?.total ?? value),
    }));
  }

  return [];
};

const normalizeDateWisePublished = (input) => {
  if (Array.isArray(input)) {
    return input.map((item, index) => ({
      label: item?.label || item?.date || item?.day || item?.period || item?.bucket || `Range ${index + 1}`,
      value: toNumber(item?.published_count ?? item?.count ?? item?.total ?? item?.value),
    }));
  }

  if (input && typeof input === "object") {
    return Object.entries(input).map(([key, value]) => ({
      label: key,
      value: toNumber(value?.published_count ?? value?.count ?? value?.total ?? value?.value ?? value),
    }));
  }

  return [];
};

const normalizeLatestMatches = (input) =>
  (Array.isArray(input) ? input : []).map((item) => ({
    candidate_name: item?.candidate_name || item?.name || "Unknown",
    candidate_id: item?.candidate_id || item?.user_id || item?.candidateId || "",
    match_score: item?.match_score ?? item?.score ?? "—",
    compatibility_level: item?.compatibility_level || item?.compatibility || "—",
    source_service: item?.source_service || item?.service || "—",
    published_at: item?.published_at || item?.updated_at || item?.created_at || "",
  }));

const normalizeSummaryData = (response) => {
  const payload = extractPayload(response);
  const totals = payload?.totals || {};
  const publishedMatches = toNumber(totals.published_matches ?? totals.total_published_matches ?? payload.published_matches);

  return {
    user: payload?.user || {},
    totals: {
      active_matches: toNumber(totals.active_matches ?? totals.total_active_matches ?? payload.active_matches),
      published_matches: publishedMatches,
      unpublished_matches: toNumber(totals.unpublished_matches ?? totals.total_unpublished_matches ?? payload.unpublished_matches),
      filtered_published_matches: toNumber(
        totals.filtered_published_matches ??
          totals.filtered_matches ??
          totals.published_filtered_matches ??
          payload.filtered_published_matches ??
          publishedMatches,
      ),
      first_published_at: totals.first_published_at || payload.first_published_at || "",
      last_published_at: totals.last_published_at || payload.last_published_at || "",
    },
    date_wise_published: normalizeDateWisePublished(payload?.date_wise_published),
    compatibility_breakdown: normalizeBreakdown(payload?.compatibility_breakdown),
    source_service_breakdown: normalizeBreakdown(payload?.source_service_breakdown),
    latest_published_matches: normalizeLatestMatches(payload?.latest_published_matches),
  };
};

const normalizeHistoryData = (response, fallbackLimit, fallbackOffset) => {
  const payload = extractPayload(response);
  const dataRoot = Array.isArray(payload) ? { rows: payload } : payload;
  const rows =
    dataRoot?.rows ||
    dataRoot?.items ||
    dataRoot?.matches ||
    dataRoot?.history ||
    dataRoot?.records ||
    (Array.isArray(payload?.data) ? payload.data : []) ||
    [];
  const pagination = response?.pagination || dataRoot?.pagination || response?.meta || dataRoot?.meta || {};

  const limit = toNumber(pagination.limit ?? dataRoot?.limit ?? response?.limit ?? fallbackLimit);
  const offset = toNumber(pagination.offset ?? dataRoot?.offset ?? response?.offset ?? fallbackOffset);
  const total = toNumber(pagination.total ?? pagination.count ?? dataRoot?.total ?? dataRoot?.count ?? response?.total ?? response?.count);

  return {
    rows: Array.isArray(rows) ? rows : [],
    pagination: {
      limit,
      offset,
      total,
      has_more:
        pagination.has_more ??
        pagination.hasMore ??
        (total > 0 ? offset + (Array.isArray(rows) ? rows.length : 0) < total : Array.isArray(rows) && rows.length >= limit),
    },
  };
};

const createErrorDetails = (error) => ({
  status: error?.status || error?.response?.status || 0,
  message:
    error?.message ||
    error?.data?.message ||
    error?.response?.data?.message ||
    "Something went wrong while loading this section.",
});

const parseQueryState = (searchParams, routeUserId) => {
  const rawLimit = searchParams.get("limit");
  const rawOffset = searchParams.get("offset");
  const limitValue = rawLimit && !Number.isNaN(Number(rawLimit)) && Number(rawLimit) > 0 ? rawLimit : DEFAULT_LIMIT;
  const offsetValue = rawOffset && !Number.isNaN(Number(rawOffset)) && Number(rawOffset) >= 0 ? rawOffset : "0";

  return {
    selectedUserId: routeUserId || "",
    filters: {
      ...DEFAULT_FILTERS,
      uid: searchParams.get("uid") || "",
      user_id: searchParams.get("user_id") || "",
      candidate_id: searchParams.get("candidate_id") || "",
      search: searchParams.get("search") || "",
      compatibility_level: searchParams.get("compatibility_level") || "",
      source_service: searchParams.get("source_service") || "",
      decision: BOOLEAN_TOGGLE_OPTIONS.some((item) => item.value === searchParams.get("decision")) ? searchParams.get("decision") : "all",
      is_disliked: BOOLEAN_TOGGLE_OPTIONS.some((item) => item.value === searchParams.get("is_disliked")) ? searchParams.get("is_disliked") : "all",
      published: searchParams.get("published") === "false" ? "false" : "true",
      today: searchParams.get("today") === "true" ? "true" : "false",
      new: BOOLEAN_TOGGLE_OPTIONS.some((item) => item.value === searchParams.get("new")) ? searchParams.get("new") : "all",
      distance: searchParams.get("distance") || "",
      match_score_min: searchParams.get("match_score_min") || "",
      match_score_max: searchParams.get("match_score_max") || "",
      published_from: searchParams.get("published_from") || "",
      published_to: searchParams.get("published_to") || "",
      created_from: searchParams.get("created_from") || "",
      created_to: searchParams.get("created_to") || "",
      group_by: GROUP_BY_OPTIONS.some((item) => item.value === searchParams.get("group_by")) ? searchParams.get("group_by") : "day",
      sort_by: SORT_BY_OPTIONS.some((item) => item.value === searchParams.get("sort_by")) ? searchParams.get("sort_by") : "published_at",
      sort_order: SORT_ORDER_OPTIONS.some((item) => item.value === searchParams.get("sort_order")) ? searchParams.get("sort_order") : "desc",
      limit: limitValue,
      offset: offsetValue,
    },
  };
};

const buildUrlState = (filters) => ({
  uid: filters.uid || undefined,
  user_id: filters.user_id || undefined,
  candidate_id: filters.candidate_id || undefined,
  search: filters.search || undefined,
  compatibility_level: filters.compatibility_level || undefined,
  source_service: filters.source_service || undefined,
  decision: filters.decision !== DEFAULT_FILTERS.decision ? filters.decision : undefined,
  is_disliked: filters.is_disliked !== DEFAULT_FILTERS.is_disliked ? filters.is_disliked : undefined,
  published: filters.published !== DEFAULT_FILTERS.published ? filters.published : undefined,
  today: filters.today !== DEFAULT_FILTERS.today ? filters.today : undefined,
  new: filters.new !== DEFAULT_FILTERS.new ? filters.new : undefined,
  distance: filters.distance || undefined,
  match_score_min: filters.match_score_min || undefined,
  match_score_max: filters.match_score_max || undefined,
  published_from: filters.published_from || undefined,
  published_to: filters.published_to || undefined,
  created_from: filters.created_from || undefined,
  created_to: filters.created_to || undefined,
  group_by: filters.group_by !== DEFAULT_FILTERS.group_by ? filters.group_by : undefined,
  sort_by: filters.sort_by !== DEFAULT_FILTERS.sort_by ? filters.sort_by : undefined,
  sort_order: filters.sort_order !== DEFAULT_FILTERS.sort_order ? filters.sort_order : undefined,
  limit: filters.limit !== DEFAULT_FILTERS.limit ? filters.limit : undefined,
  offset: filters.offset !== DEFAULT_FILTERS.offset ? filters.offset : undefined,
});

const buildFeedFilters = (filters, debouncedUid) => ({
  uid: debouncedUid,
  user_id: filters.user_id,
  candidate_id: filters.candidate_id,
  today: filters.today === "true" ? true : undefined,
  published_from: filters.published_from,
  published_to: filters.published_to,
  new: filters.new !== "all" ? filters.new : undefined,
  distance: filters.distance,
  limit: Number(filters.limit || DEFAULT_LIMIT),
  offset: Number(filters.offset || 0),
});

const buildSummaryFilters = (filters, debouncedSearch) => ({
  candidate_id: filters.candidate_id,
  search: debouncedSearch,
  compatibility_level: filters.compatibility_level,
  source_service: filters.source_service,
  decision: filters.decision !== "all" ? filters.decision : undefined,
  is_disliked: filters.is_disliked !== "all" ? filters.is_disliked : undefined,
  match_score_min: filters.match_score_min,
  match_score_max: filters.match_score_max,
  published_from: filters.published_from,
  published_to: filters.published_to,
  group_by: filters.group_by,
});

const buildHistoryFilters = (filters, debouncedSearch) => ({
  ...buildSummaryFilters(filters, debouncedSearch),
  published: filters.published,
  created_from: filters.created_from,
  created_to: filters.created_to,
  sort_by: filters.sort_by,
  sort_order: filters.sort_order,
  limit: Number(filters.limit || DEFAULT_LIMIT),
  offset: Number(filters.offset || 0),
});

const InfoPill = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-slate-800 bg-slate-950/80 px-4 py-3">
    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
      <Icon size={13} />
      {label}
    </div>
    <p className="mt-2 text-sm font-medium text-slate-100">{value || "—"}</p>
  </div>
);

const StatCard = ({ title, value, subtitle, accent }) => (
  <div className="rounded-3xl border border-slate-800 bg-slate-950/80 p-5 shadow-[0_24px_80px_rgba(15,23,42,0.24)]">
    <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">{title}</p>
    <p className={`mt-3 text-3xl font-black tracking-tight ${accent}`}>{value}</p>
    <p className="mt-2 text-sm text-slate-500">{subtitle}</p>
  </div>
);

const FilterField = ({ label, children, helper }) => (
  <label className="block">
    <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">{label}</span>
    {children}
    {helper ? <span className="mt-2 block text-xs text-slate-500">{helper}</span> : null}
  </label>
);

const textInputClassName =
  "w-full rounded-2xl border border-slate-700 bg-slate-950/90 px-4 py-3 text-sm text-slate-100 outline-none transition placeholder:text-slate-500 focus:border-sky-400 focus:ring-2 focus:ring-sky-500/20";
const selectClassName =
  "w-full rounded-2xl border border-slate-700 bg-slate-950/90 px-4 py-3 text-sm text-slate-100 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-500/20";

export default function Matches() {
  const routeParams = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryState = useMemo(() => parseQueryState(searchParams, routeParams.userId), [routeParams.userId, searchParams]);
  const { selectedUserId, filters } = queryState;
  const isFeedMode = !selectedUserId;

  const [summaryData, setSummaryData] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [pagination, setPagination] = useState({ limit: Number(DEFAULT_LIMIT), offset: 0, total: 0, has_more: false });
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [errorSummary, setErrorSummary] = useState(null);
  const [errorHistory, setErrorHistory] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const activeTextFilter = isFeedMode ? filters.uid : filters.search;
  const [debouncedSearch, setDebouncedSearch] = useState(activeTextFilter);
  const activeRequestRef = useRef(0);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(activeTextFilter);
    }, 400);
    return () => window.clearTimeout(timeoutId);
  }, [activeTextFilter]);

  const syncUrlState = useCallback(
    (nextFilters) => {
      const queryString = buildQueryString(buildUrlState(nextFilters));
      setSearchParams(new URLSearchParams(queryString), { replace: true });
    },
    [setSearchParams],
  );

  const updateFilters = useCallback(
    (patch, options = {}) => {
      const shouldResetOffset = options.resetOffset !== false;
      const nextFilters = { ...filters, ...patch, ...(shouldResetOffset ? { offset: "0" } : {}) };
      syncUrlState(nextFilters);
    },
    [filters, syncUrlState],
  );

  const resetFilters = useCallback(() => {
    syncUrlState({ ...DEFAULT_FILTERS });
  }, [syncUrlState]);

  const refetchCurrent = useCallback(() => {
    setRetryNonce((value) => value + 1);
  }, []);

  const numericLimit = useMemo(() => Number(filters.limit || DEFAULT_LIMIT), [filters.limit]);
  const numericOffset = useMemo(() => Number(filters.offset || 0), [filters.offset]);
  const feedQueryFilters = useMemo(
    () => buildFeedFilters(filters, debouncedSearch),
    [filters, debouncedSearch],
  );

  const summaryQueryFilters = useMemo(
    () => buildSummaryFilters(filters, debouncedSearch),
    [filters, debouncedSearch],
  );

  const historyQueryFilters = useMemo(
    () => buildHistoryFilters(filters, debouncedSearch),
    [filters, debouncedSearch],
  );

  useEffect(() => {
    const requestId = activeRequestRef.current + 1;
    activeRequestRef.current = requestId;

    setLoadingSummary(!isFeedMode);
    setLoadingHistory(true);
    setErrorSummary(null);
    setErrorHistory(null);

    if (isFeedMode) {
      setSummaryData(null);

      getMatchmakingResultsFeed(feedQueryFilters)
        .then((response) => {
          if (activeRequestRef.current !== requestId) {
            return;
          }

          const normalizedFeed = normalizeHistoryData(response, numericLimit, numericOffset);
          setHistoryRows(normalizedFeed.rows);
          setPagination(normalizedFeed.pagination);
        })
        .catch((error) => {
          if (activeRequestRef.current !== requestId) {
            return;
          }

          setHistoryRows([]);
          setPagination({ limit: numericLimit, offset: numericOffset, total: 0, has_more: false });
          setErrorHistory(createErrorDetails(error));
        })
        .finally(() => {
          if (activeRequestRef.current !== requestId) {
            return;
          }

          setLoadingSummary(false);
          setLoadingHistory(false);
        });

      return;
    }

    Promise.allSettled([
      getMatchPublishSummary(selectedUserId, summaryQueryFilters),
      getMatchPublishHistory(selectedUserId, historyQueryFilters),
    ]).then(([summaryResult, historyResult]) => {
      if (activeRequestRef.current !== requestId) {
        return;
      }

      let userNotFound = false;

      if (summaryResult.status === "fulfilled") {
        setSummaryData(normalizeSummaryData(summaryResult.value));
      } else {
        const details = createErrorDetails(summaryResult.reason);
        userNotFound = details.status === 404;
        setSummaryData(null);
        setErrorSummary(details);
      }

      if (userNotFound) {
        setHistoryRows([]);
        setPagination({ limit: numericLimit, offset: numericOffset, total: 0, has_more: false });
        setErrorHistory(null);
      } else if (historyResult.status === "fulfilled") {
        const normalizedHistory = normalizeHistoryData(historyResult.value, numericLimit, numericOffset);
        setHistoryRows(normalizedHistory.rows);
        setPagination(normalizedHistory.pagination);
      } else {
        setHistoryRows([]);
        setPagination({ limit: numericLimit, offset: numericOffset, total: 0, has_more: false });
        setErrorHistory(createErrorDetails(historyResult.reason));
      }

      setLoadingSummary(false);
      setLoadingHistory(false);
    });
  }, [
    isFeedMode,
    selectedUserId,
    feedQueryFilters,
    historyQueryFilters,
    numericLimit,
    numericOffset,
    retryNonce,
    summaryQueryFilters,
  ]);

  const formatDateTime = useCallback((value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(date);
  }, []);

  const adminTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "Local timezone", []);
  const userMeta = summaryData?.user || {};
  const totals = summaryData?.totals || {};
  const latestPublishedMatches = summaryData?.latest_published_matches || [];
  const isUserNotFound = errorSummary?.status === 404;
  const currentSearch = searchParams.toString();
  const fromFeed = searchParams.get("from_feed") === "true";
  const feedReturn = searchParams.get("feed_return") || "";
  const feedRow = searchParams.get("feed_row") || "";
  const feedRowId = searchParams.get("feed_row_id") || "";
  const feedUser = searchParams.get("feed_user") || "";
  const feedCandidate = searchParams.get("feed_candidate") || "";
  const highlightedRowNumber = searchParams.get("highlight_row") || "";
  const highlightedRowId = searchParams.get("highlight_row_id") || "";

  const handleBackToFeed = useCallback(() => {
    const nextSearchParams = new URLSearchParams(feedReturn);

    if (feedRow) {
      nextSearchParams.set("highlight_row", feedRow);
    }
    if (feedRowId) {
      nextSearchParams.set("highlight_row_id", feedRowId);
    }
    if (feedUser) {
      nextSearchParams.set("highlight_user", feedUser);
    }
    if (feedCandidate) {
      nextSearchParams.set("highlight_candidate", feedCandidate);
    }

    const nextSearch = nextSearchParams.toString();
    navigate(`/matches${nextSearch ? `?${nextSearch}` : ""}`);
  }, [feedCandidate, feedReturn, feedRow, feedRowId, feedUser, navigate]);

  return (
    <div className="min-h-screen space-y-6 pb-8 text-slate-100">
      <section className="overflow-hidden rounded-[32px] border border-slate-800 bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.18),_transparent_35%),radial-gradient(circle_at_top_right,_rgba(129,140,248,0.18),_transparent_34%),linear-gradient(145deg,_rgba(2,6,23,0.98),_rgba(15,23,42,0.94))] shadow-[0_24px_120px_rgba(15,23,42,0.4)]">
        <div className="grid gap-8 px-6 py-7 xl:grid-cols-[1.25fr_0.95fr] xl:px-8">
          <div>
            {!isFeedMode && fromFeed ? (
              <button
                type="button"
                onClick={handleBackToFeed}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-700 bg-slate-900/90 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:border-sky-500 hover:text-white"
              >
                <ChevronLeft size={15} />
                Back To Feed
              </button>
            ) : null}
            <div className="inline-flex items-center gap-2 rounded-full border border-sky-500/20 bg-sky-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.28em] text-sky-300">
              <Sparkles size={12} />
              {isFeedMode ? "Admin Match Feed" : "Match Publishing Analytics"}
            </div>
            <h1 className="mt-4 text-3xl font-black tracking-tight text-white md:text-4xl">
              {isFeedMode ? "Published matches across all users" : "Admin view for one user&apos;s published match performance"}
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-300 md:text-base">
              {isFeedMode
                ? "Review today&apos;s published matches or any published date range across the full admin feed. Search by UID across user and candidate, then jump into a specific user&apos;s analytics from any row."
                : "Inspect publishing totals, date-wise trends, compatibility splits, source-service breakdowns, and a detailed history table for one selected user. Filters stay in the URL so the page is shareable."}
            </p>
            <div className="mt-5 flex flex-wrap gap-3 text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
              <span className="rounded-full border border-slate-700 bg-slate-950/70 px-3 py-2">Local timezone: {adminTimezone}</span>
              <span className="rounded-full border border-slate-700 bg-slate-950/70 px-3 py-2">
                {isFeedMode ? "Feed defaults to today=false" : "History defaults to published=true"}
              </span>
              {!isFeedMode && fromFeed && feedRow ? (
                <span className="rounded-full border border-sky-500/20 bg-sky-500/10 px-3 py-2 text-sky-300">
                  Opened from row #{feedRow}
                </span>
              ) : null}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {isFeedMode ? (
              <>
                <InfoPill icon={Globe2} label="Feed Scope" value="Cross-user published matchmaking feed" />
                <InfoPill icon={Layers3} label="UID Search" value={filters.uid || "Search across user_id and candidate_id"} />
                <InfoPill icon={CalendarDays} label="Published Window" value={filters.today === "true" ? "Today only" : `${filters.published_from || "Any"} to ${filters.published_to || "Any"}`} />
                <InfoPill icon={Activity} label="Rows Loaded" value={loadingHistory ? "Loading..." : `${historyRows.length} rows`} />
              </>
            ) : (
              <>
            <InfoPill icon={UserCircle2} label="Selected User" value={userMeta.name || userMeta.username || "Choose a user to load analytics"} />
            <InfoPill icon={Layers3} label="User ID" value={userMeta.user_id || userMeta.userId || selectedUserId || "—"} />
            <InfoPill icon={Phone} label="Phone Number" value={userMeta.phone_number || userMeta.phone || userMeta.user_phone || "—"} />
            <InfoPill
              icon={CalendarDays}
              label="Assigned / Created"
              value={`${userMeta.assigned_to || "Unassigned"} · ${formatDateTime(userMeta.created_at || userMeta.createdAt)}`}
            />
            {fromFeed ? (
              <InfoPill
                icon={Activity}
                label="Clicked Feed Row"
                value={`Row ${feedRow || "--"} | ${feedUser || "Unknown"}${feedCandidate ? ` -> ${feedCandidate}` : ""}`}
              />
            ) : null}
              </>
            )}
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-slate-800 bg-slate-950/80 p-5 shadow-[0_24px_80px_rgba(15,23,42,0.28)]">
        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">Filters</h2>
            <p className="mt-1 text-sm text-slate-400">
              {isFeedMode
                ? "Filter the admin feed by UID, exact user or candidate IDs, published date window, and feed-specific flags."
                : "Update filters to refresh summary analytics and history together. Search is debounced."}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-700 bg-slate-900 px-4 py-2.5 text-sm font-semibold text-slate-200 transition hover:border-slate-500 hover:text-white"
            >
              Reset filters
            </button>
            <button
              type="button"
              onClick={refetchCurrent}
              className="inline-flex items-center gap-2 rounded-2xl bg-sky-500 px-4 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-sky-400"
            >
              <RefreshCw size={15} />
              Apply filters
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {isFeedMode ? (
            <>
              <FilterField label="UID Search" helper="Matches across both user_id and candidate_id.">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                  <input type="text" value={filters.uid} onChange={(event) => updateFilters({ uid: event.target.value })} placeholder="U123" className={`${textInputClassName} pl-11`} />
                </div>
              </FilterField>
              <FilterField label="User ID">
                <input type="text" value={filters.user_id} onChange={(event) => updateFilters({ user_id: event.target.value })} placeholder="Exact user ID" className={textInputClassName} />
              </FilterField>
              <FilterField label="Candidate ID">
                <input type="text" value={filters.candidate_id} onChange={(event) => updateFilters({ candidate_id: event.target.value })} placeholder="Exact candidate ID" className={textInputClassName} />
              </FilterField>
              <FilterField label="Today Only">
                <select value={filters.today} onChange={(event) => updateFilters({ today: event.target.value })} className={selectClassName}>
                  <option value="true">True</option>
                  <option value="false">False</option>
                </select>
              </FilterField>
              <FilterField label="Published From">
                <input type="date" value={filters.published_from} onChange={(event) => updateFilters({ published_from: event.target.value })} className={textInputClassName} />
              </FilterField>
              <FilterField label="Published To">
                <input type="date" value={filters.published_to} onChange={(event) => updateFilters({ published_to: event.target.value })} className={textInputClassName} />
              </FilterField>
              <FilterField label="New">
                <select value={filters.new} onChange={(event) => updateFilters({ new: event.target.value })} className={selectClassName}>
                  {BOOLEAN_TOGGLE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </FilterField>
              <FilterField label="Distance">
                <input type="text" value={filters.distance} onChange={(event) => updateFilters({ distance: event.target.value })} placeholder="e.g. local, near, 100" className={textInputClassName} />
              </FilterField>
            </>
          ) : (
            <>
              <FilterField label="Selected User ID" helper="This analytics page is route-driven.">
                <input type="text" value={selectedUserId} readOnly className={`${textInputClassName} cursor-not-allowed opacity-80`} />
              </FilterField>
              <FilterField label="Candidate ID">
                <input type="text" value={filters.candidate_id} onChange={(event) => updateFilters({ candidate_id: event.target.value })} placeholder="Candidate ID" className={textInputClassName} />
              </FilterField>
              <FilterField label="Search" helper="Candidate name, candidate id, or source service.">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                  <input type="text" value={filters.search} onChange={(event) => updateFilters({ search: event.target.value })} placeholder="rahul, C1234, source service..." className={`${textInputClassName} pl-11`} />
                </div>
              </FilterField>
              <FilterField label="Compatibility Level">
                <select value={filters.compatibility_level} onChange={(event) => updateFilters({ compatibility_level: event.target.value })} className={selectClassName}>
                  {COMPATIBILITY_OPTIONS.map((option) => (
                    <option key={option.value || "all"} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </FilterField>
              <FilterField label="Source Service">
                <input type="text" value={filters.source_service} onChange={(event) => updateFilters({ source_service: event.target.value })} placeholder="source service" className={textInputClassName} />
              </FilterField>
              <FilterField label="Decision">
                <select value={filters.decision} onChange={(event) => updateFilters({ decision: event.target.value })} className={selectClassName}>
                  {BOOLEAN_TOGGLE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </FilterField>
              <FilterField label="Disliked">
                <select value={filters.is_disliked} onChange={(event) => updateFilters({ is_disliked: event.target.value })} className={selectClassName}>
                  {BOOLEAN_TOGGLE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </FilterField>
              <FilterField label="Group By">
                <select value={filters.group_by} onChange={(event) => updateFilters({ group_by: event.target.value })} className={selectClassName}>
                  {GROUP_BY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </FilterField>
              <FilterField label="Match Score Min">
                <input type="number" value={filters.match_score_min} onChange={(event) => updateFilters({ match_score_min: event.target.value })} placeholder="0" className={textInputClassName} />
              </FilterField>
              <FilterField label="Match Score Max">
                <input type="number" value={filters.match_score_max} onChange={(event) => updateFilters({ match_score_max: event.target.value })} placeholder="100" className={textInputClassName} />
              </FilterField>
              <FilterField label="Published From">
                <input type="date" value={filters.published_from} onChange={(event) => updateFilters({ published_from: event.target.value })} className={textInputClassName} />
              </FilterField>
              <FilterField label="Published To">
                <input type="date" value={filters.published_to} onChange={(event) => updateFilters({ published_to: event.target.value })} className={textInputClassName} />
              </FilterField>
              <FilterField label="Created From">
                <input type="date" value={filters.created_from} onChange={(event) => updateFilters({ created_from: event.target.value })} className={textInputClassName} />
              </FilterField>
              <FilterField label="Created To">
                <input type="date" value={filters.created_to} onChange={(event) => updateFilters({ created_to: event.target.value })} className={textInputClassName} />
              </FilterField>
              <FilterField label="Sort By">
                <select value={filters.sort_by} onChange={(event) => updateFilters({ sort_by: event.target.value })} className={selectClassName}>
                  {SORT_BY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </FilterField>
              <FilterField label="Sort Order">
                <select value={filters.sort_order} onChange={(event) => updateFilters({ sort_order: event.target.value })} className={selectClassName}>
                  {SORT_ORDER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </FilterField>
            </>
          )}
        </div>
      </section>

      {isFeedMode ? (
        <MatchesFeedTable
          rows={historyRows}
          pagination={pagination}
          loading={loadingHistory}
          error={errorHistory}
          onRetry={refetchCurrent}
          onPageChange={(nextOffset) => updateFilters({ offset: String(nextOffset) }, { resetOffset: false })}
          onLimitChange={(nextLimit) => updateFilters({ limit: String(nextLimit), offset: "0" }, { resetOffset: false })}
          formatDateTime={formatDateTime}
          currentSearch={currentSearch}
          highlightedRowId={highlightedRowId}
          highlightedRowNumber={highlightedRowNumber}
        />
      ) : isUserNotFound ? (
        <section className="rounded-3xl border border-rose-500/20 bg-rose-500/10 px-6 py-10">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="flex items-start gap-4">
              <div className="rounded-2xl border border-rose-500/20 bg-rose-500/10 p-3 text-rose-300">
                <AlertTriangle size={20} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">User not found</h2>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-rose-100/90">
                  The summary API returned a 404 for user <span className="font-semibold">{selectedUserId}</span>. Verify the user ID and try again.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={refetchCurrent}
              className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-slate-100"
            >
              <RefreshCw size={15} />
              Retry
            </button>
          </div>
        </section>
      ) : (
        <>
          <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            <StatCard title="Total Active Matches" value={loadingSummary ? "..." : totals.active_matches ?? 0} subtitle="Current active match inventory for this user." accent="text-sky-300" />
            <StatCard title="Total Published Matches" value={loadingSummary ? "..." : totals.published_matches ?? 0} subtitle="All published matches returned by the summary totals." accent="text-emerald-300" />
            <StatCard title="Total Unpublished Matches" value={loadingSummary ? "..." : totals.unpublished_matches ?? 0} subtitle="Remaining active matches that are not published." accent="text-amber-300" />
            <StatCard title="Filtered Published Matches" value={loadingSummary ? "..." : totals.filtered_published_matches ?? 0} subtitle="Published matches matching the current filter set." accent="text-indigo-300" />
            <StatCard title="First Published At" value={loadingSummary ? "..." : formatDateTime(totals.first_published_at)} subtitle="Earliest published match in the filtered dataset." accent="text-slate-100" />
            <StatCard title="Last Published At" value={loadingSummary ? "..." : formatDateTime(totals.last_published_at)} subtitle="Most recent published match in the filtered dataset." accent="text-slate-100" />
          </section>

          {errorSummary && !loadingSummary ? (
            <section className="rounded-3xl border border-amber-500/20 bg-amber-500/10 px-6 py-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex items-start gap-3">
                  <div className="rounded-2xl bg-amber-500/10 p-3 text-amber-300">
                    <AlertTriangle size={18} />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-white">Summary API issue</h2>
                    <p className="mt-1 text-sm text-amber-100/90">{errorSummary.message}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={refetchCurrent}
                  className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-slate-100"
                >
                  <RefreshCw size={14} />
                  Retry summary
                </button>
              </div>
            </section>
          ) : null}

          <MatchesCharts
            dateWisePublished={summaryData?.date_wise_published || []}
            compatibilityBreakdown={summaryData?.compatibility_breakdown || []}
            sourceServiceBreakdown={summaryData?.source_service_breakdown || []}
          />

          <section className="rounded-3xl border border-slate-800 bg-slate-950/80 p-5 shadow-[0_24px_80px_rgba(15,23,42,0.28)]">
            <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-white">Latest Published Preview</h2>
                <p className="mt-1 text-sm text-slate-400">Recent published candidates from the summary payload for a quick review pass.</p>
              </div>
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
                <Activity size={13} />
                {latestPublishedMatches.length} recent records
              </div>
            </div>

            {latestPublishedMatches.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-slate-700 bg-slate-950/50 px-6 py-10 text-center">
                <p className="text-base font-semibold text-white">No recent published matches</p>
                <p className="mt-2 text-sm text-slate-400">The latest preview will populate when the summary response includes recent published matches.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
                {latestPublishedMatches.map((match, index) => (
                  <div key={`${match.candidate_id || match.candidate_name}-${index}`} className="rounded-3xl border border-slate-800 bg-[linear-gradient(180deg,_rgba(15,23,42,0.9),_rgba(2,6,23,0.9))] p-5">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Candidate</p>
                      <p className="mt-2 text-lg font-semibold text-white">{match.candidate_name}</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {match.candidate_id ? (
                          <Link to={`/users/user/${match.candidate_id}`} className="inline-flex items-center gap-2 rounded-full border border-sky-500/20 bg-sky-500/10 px-3 py-1 text-xs font-semibold text-sky-300 transition hover:bg-sky-500/20">
                            <UserCircle2 size={12} />
                            {match.candidate_id}
                          </Link>
                        ) : null}
                        <span className="inline-flex items-center rounded-full border border-slate-700 bg-slate-900 px-3 py-1 text-xs font-semibold text-slate-300">
                          Score: {match.match_score}
                        </span>
                      </div>
                    </div>
                    <div className="mt-5 grid grid-cols-2 gap-3">
                      <div className="rounded-2xl border border-slate-800 bg-slate-950/80 p-3">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Compatibility</p>
                        <p className="mt-2 text-sm font-medium text-slate-100">{match.compatibility_level}</p>
                      </div>
                      <div className="rounded-2xl border border-slate-800 bg-slate-950/80 p-3">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Source</p>
                        <p className="mt-2 text-sm font-medium text-slate-100">{match.source_service}</p>
                      </div>
                    </div>
                    <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-950/80 p-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Published At</p>
                      <p className="mt-2 text-sm font-medium text-slate-100">{formatDateTime(match.published_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <MatchesHistoryTable
            rows={historyRows}
            pagination={pagination}
            loading={loadingHistory}
            error={errorHistory}
            onRetry={refetchCurrent}
            onPageChange={(nextOffset) => updateFilters({ offset: String(nextOffset) }, { resetOffset: false })}
            onLimitChange={(nextLimit) => updateFilters({ limit: String(nextLimit), offset: "0" }, { resetOffset: false })}
            formatDateTime={formatDateTime}
          />
        </>
      )}
    </div>
  );
}
